import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Papa from 'papaparse';
import { api, type SequencerCatalogEntry, type SequencerKey, TenantBulkRow, useClients } from '../lib/api';
import { useRole } from '../lib/role';


/** Build the sequencer-account picker options.
 *
 * Each picker entry is a *specific account*, not just a sequencer. So a
 * workspace with two Instantly keys ("Acme Prod" + "Acme Sandbox") sees
 * both as separate entries, plus a fallback "Instantly · workspace
 * default" entry that resolves at runtime.
 *
 * The picker `value` is encoded as `<sequencer>::<key_id>` for keyed
 * options or `<sequencer>::default` for the default-fallback option.
 * `sequencer` and `sequencer_key_id` are decoded at submit time.
 */
type SeqOption =
  | { kind: 'key'; sequencer: string; keyId: string; label: string; last4: string | null; isDefault: boolean }
  | { kind: 'default'; sequencer: string; sequencerName: string; configured: boolean }
  | { kind: 'planned'; sequencer: string; sequencerName: string };

function useSequencerOptions() {
  const me = useQuery({ queryKey: ['users-me'], queryFn: api.userMe, retry: false });
  const principal: any = me.data;
  const clientId: string | null = principal && 'client_id' in principal ? principal.client_id : null;

  const catalog = useQuery({ queryKey: ['sequencer-catalog'], queryFn: api.listSequencerCatalog });
  const keys = useQuery({
    queryKey: ['sequencer-keys', clientId],
    queryFn: () => api.listSequencerKeys(clientId!),
    enabled: !!clientId,
  });

  const cat: SequencerCatalogEntry[] = catalog.data || [];
  const ks: SequencerKey[] = keys.data || [];

  const options: SeqOption[] = [];
  for (const s of cat) {
    if (s.status !== 'live') {
      options.push({ kind: 'planned', sequencer: s.slug, sequencerName: s.name });
      continue;
    }
    const slugKeys = ks.filter(k => k.sequencer === s.slug)
      .sort((a, b) => Number(b.is_default) - Number(a.is_default));
    for (const k of slugKeys) {
      options.push({
        kind: 'key', sequencer: s.slug, keyId: k.id,
        label: `${s.name}${k.label ? ' · ' + k.label : ' · (unlabeled)'}`,
        last4: k.last4, isDefault: k.is_default,
      });
    }
    if (slugKeys.length !== 1) {
      // Show a "use default" fallback when there are 0 or >1 keys; with
      // exactly 1 key the fallback is redundant.
      options.push({
        kind: 'default', sequencer: s.slug, sequencerName: s.name,
        configured: slugKeys.length > 0,
      });
    }
  }

  // Default selection: first configured key, else the first 'default'
  // entry, else the first option.
  const firstConfigured = options.find(o => o.kind === 'key');
  const defaultValue = firstConfigured
    ? encodeOption(firstConfigured)
    : (options.find(o => o.kind === 'default') ? encodeOption(options.find(o => o.kind === 'default')!) : 'instantly::default');

  return {
    options, defaultValue,
    hasAnyKey: ks.length > 0,
    loaded: catalog.isFetched && (!clientId || keys.isFetched),
  };
}

function encodeOption(o: SeqOption): string {
  if (o.kind === 'key') return `${o.sequencer}::${o.keyId}`;
  return `${o.sequencer}::default`;
}

function decodeOption(value: string): { sequencer: string; sequencerKeyId?: string } {
  const [sequencer, tail] = value.split('::');
  if (!tail || tail === 'default') return { sequencer };
  return { sequencer, sequencerKeyId: tail };
}

type Mode = 'single' | 'bulk';

export default function NewTenant() {
  const [mode, setMode] = useState<Mode>('single');
  const { isAdmin } = useRole();
  return (
    <div className="p-8 max-w-4xl mx-auto">
      <header className="mb-6">
        <h1 className="page-h1 text-xl">New tenant</h1>
        <p className="page-sub mt-1">
          {isAdmin
            ? 'Onboard one or many Microsoft 365 tenants. Each runs the full provisioning pipeline and OAuths every mailbox into the chosen sequencer.'
            : 'Add a Microsoft 365 tenant. Pick how many mailboxes you want (25–150) and we provision and connect them automatically.'}
        </p>
      </header>
      <div className="inline-flex p-1 mb-5 rounded-lg border border-line bg-panel">
        <button
          onClick={() => setMode('single')}
          className={`px-4 py-1.5 rounded-md text-xs font-medium transition ${
            mode === 'single' ? 'bg-accent text-accent-fg shadow-sm' : 'text-dim hover:text-ink'
          }`}
        >Single tenant</button>
        <button
          onClick={() => setMode('bulk')}
          className={`px-4 py-1.5 rounded-md text-xs font-medium transition ${
            mode === 'bulk' ? 'bg-accent text-accent-fg shadow-sm' : 'text-dim hover:text-ink'
          }`}
        >Bulk paste (CSV)</button>
      </div>
      {mode === 'single' ? <Single /> : <Bulk />}
    </div>
  );
}

function Single() {
  const nav = useNavigate();
  const { isAdmin } = useRole();
  const { data: clients } = useClients();
  const seq = useSequencerOptions();
  // `initial_password` is only useful when an existing tenant's GA password
  // has already been rotated away from the Microsoft-issued initial. For
  // brand-new tenants the two values are identical, so we just send
  // ga_password and let the backend default initial_password to it.
  // Admins still get the explicit field for the rare existing-tenant case.
  const [form, setForm] = useState({ ga_upn: '', ga_password: '', initial_password: '', target_domain: '', name_seed: '' });
  const [mailboxCount, setMailboxCount] = useState<number>(100);
  const [clientSlug, setClientSlug] = useState('');
  // Encoded as `<sequencer>::<key_id>` or `<sequencer>::default`.
  const [seqValue, setSeqValue] = useState<string>('instantly::default');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (seq.loaded) setSeqValue(seq.defaultValue);
  }, [seq.loaded, seq.defaultValue]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null);
    const decoded = decodeOption(seqValue);
    try {
      const t = await api.createTenant({
        ga_upn: form.ga_upn,
        ga_password: form.ga_password,
        initial_password: form.initial_password || undefined,
        target_domain: form.target_domain,
        display_name: form.target_domain,
        name_seed: form.name_seed.trim() || undefined,
        mailbox_count: Math.max(25, Math.min(150, mailboxCount || 100)),
        client_slug: clientSlug || undefined,
        sequencer: decoded.sequencer,
        sequencer_key_id: decoded.sequencerKeyId || undefined,
      });
      nav(`/tenants/${t.id}`);
    } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  }

  const field = (key: keyof typeof form, label: string, type = 'text', placeholder = '') => (
    <label className="block">
      <div className="text-xs text-dim mb-1">{label}</div>
      <input className="input" type={type} placeholder={placeholder}
        value={form[key]} onChange={e => setForm({ ...form, [key]: e.target.value })} />
    </label>
  );

  return (
    <form onSubmit={submit} className="card p-5 space-y-3">
      {field('ga_upn', 'Tenant login', 'email', 'admin@xxx.onmicrosoft.com')}
      {field('ga_password', 'Tenant password', 'password', 'tenant admin password')}
      {isAdmin && (
        <>
          {field('initial_password', 'Initial password from Microsoft (only if rotated away from initial)', 'password', 'leave blank if same as Tenant password')}
          <div className="text-xs text-dim">
            Brand-new tenants: leave <em>Initial password</em> blank — we'll reuse <em>Tenant password</em>. Only fill it for existing tenants whose admin password has already been rotated.
          </div>
        </>
      )}
      {field('target_domain', 'Target domain', 'text', 'e.g. gospartanleadz.com')}
      <label className="block">
        <div className="text-xs text-dim mb-1">Sequencer account</div>
        <select className="input" value={seqValue} onChange={e => setSeqValue(e.target.value)}>
          {seq.options.map(o => {
            if (o.kind === 'key') {
              return (
                <option key={`k-${o.keyId}`} value={`${o.sequencer}::${o.keyId}`}>
                  {o.label}{o.last4 ? ` · …${o.last4}` : ''}{o.isDefault ? ' · default' : ''}
                </option>
              );
            }
            if (o.kind === 'default') {
              return (
                <option key={`d-${o.sequencer}`} value={`${o.sequencer}::default`}>
                  {o.sequencerName} · workspace default{o.configured ? '' : ' (no key configured)'}
                </option>
              );
            }
            return (
              <option key={`p-${o.sequencer}`} value={`${o.sequencer}::default`} disabled>
                {o.sequencerName} · coming soon
              </option>
            );
          })}
        </select>
        {!seq.hasAnyKey && (
          <div className="text-[11px] text-warn mt-1.5">
            No API key configured yet — add one in <a href="/settings" className="underline">Settings</a> before running OAuth.
          </div>
        )}
      </label>
      <label className="block">
        <div className="text-xs text-dim mb-1">Mailbox name(s) (optional)</div>
        <input
          className="input" type="text"
          placeholder="e.g. Gavin Belson, Jane Doe, John Smith — comma-separated"
          value={form.name_seed}
          onChange={e => setForm({ ...form, name_seed: e.target.value })}
        />
        <div className="text-[11px] text-dim mt-1">
          One name → all mailboxes display as that person.
          Multiple comma-separated names → mailbox count is split evenly across them.
          Leave blank for fully random names.
        </div>
      </label>
      <label className="block">
        <div className="text-xs text-dim mb-1">Number of mailboxes</div>
        <div className="flex items-center gap-3">
          <input
            type="range" min={25} max={150} step={5}
            value={mailboxCount}
            onChange={e => setMailboxCount(parseInt(e.target.value, 10))}
            className="flex-1 accent-accent"
          />
          <input
            type="number" min={25} max={150} step={1}
            value={mailboxCount}
            onChange={e => setMailboxCount(parseInt(e.target.value || '100', 10))}
            className="input w-20 text-center"
          />
        </div>
        <div className="text-[11px] text-dim mt-1">
          Min 25, max 150. Each mailbox costs one Microsoft license, so pick the
          number you actually plan to send from.
        </div>
      </label>
      {isAdmin && (
        <label className="block">
          <div className="text-xs text-dim mb-1">Client (optional)</div>
          <select className="input" value={clientSlug} onChange={e => setClientSlug(e.target.value)}>
            <option value="">— Unassigned —</option>
            {(clients || []).map(c => (
              <option key={c.id} value={c.slug}>{c.name}</option>
            ))}
          </select>
        </label>
      )}
      {err && <div className="text-err text-xs">{err}</div>}
      <button disabled={busy} className="btn-primary">{isAdmin ? 'Create + start bootstrap' : 'Add tenant'}</button>
    </form>
  );
}

type ParsedRow = TenantBulkRow & { _errors: string[] };

const ALIASES: Record<string, keyof TenantBulkRow> = {
  ga_upn: 'ga_upn', admin: 'ga_upn', admin_upn: 'ga_upn', global_admin: 'ga_upn', tenant_login: 'ga_upn',
  ga_password: 'ga_password', admin_password: 'ga_password', password: 'ga_password', tenant_password: 'ga_password',
  initial_password: 'initial_password', initial_pw: 'initial_password',
  target_domain: 'target_domain', domain: 'target_domain', sending_domain: 'target_domain', spaceship_domain: 'target_domain',
  sequencer: 'sequencer', platform: 'sequencer',
  sequencer_key_label: 'sequencer_key_label', account: 'sequencer_key_label',
  account_label: 'sequencer_key_label', sequencer_account: 'sequencer_key_label',
  instantly_account: 'sequencer_key_label', key_label: 'sequencer_key_label',
};

function normalizeHeader(h: string): keyof TenantBulkRow | null {
  const k = h.trim().toLowerCase().replace(/[\s-]/g, '_');
  return ALIASES[k] || null;
}

function validateRow(r: Partial<TenantBulkRow>): string[] {
  const errs: string[] = [];
  if (!r.ga_upn) errs.push('ga_upn missing');
  else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(r.ga_upn)) errs.push('ga_upn invalid');
  if (!r.ga_password) errs.push('ga_password missing');
  if (!r.target_domain) errs.push('target_domain missing');
  else if (!/^[a-z0-9-]+(\.[a-z0-9-]+)+$/i.test(r.target_domain)) errs.push('target_domain invalid');
  return errs;
}

function Bulk() {
  const nav = useNavigate();
  const { isAdmin } = useRole();
  const { data: clients } = useClients();
  const [raw, setRaw] = useState('');
  const [rows, setRows] = useState<ParsedRow[] | null>(null);
  const [defaultClient, setDefaultClient] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function parse() {
    setErr(null);
    const res = Papa.parse<string[]>(raw.trim(), { header: false, skipEmptyLines: true });
    if (res.errors.length) { setErr(res.errors[0].message); return; }
    // Detect a header row and, when present, map every column by name via
    // ALIASES. This is the only way to disambiguate string-y fields like
    // sequencer_key_label from name_seed in content-only mode.
    const firstRow = res.data[0] || [];
    const headerMap: Array<keyof TenantBulkRow | null> = firstRow.map(c => normalizeHeader(c || ''));
    const hasHeader = headerMap.some(Boolean);
    const dataRows = hasHeader ? res.data.slice(1) : res.data;
    if (hasHeader) {
      const parsed: ParsedRow[] = dataRows.map(cols => {
        const row: any = {};
        cols.forEach((cell, i) => {
          const key = headerMap[i];
          if (!key) return;
          const v = (cell || '').trim();
          if (v) row[key] = v;
        });
        const errors = validateRow(row);
        return { ...row, _errors: errors } as ParsedRow;
      });
      const seen = new Set<string>();
      for (const r of parsed) {
        if (r.target_domain) {
          if (seen.has(r.target_domain)) r._errors.push('duplicate target_domain');
          else seen.add(r.target_domain);
        }
      }
      setRows(parsed);
      setRaw('');
      return;
    }

    // Classify each cell by content rather than fixed column order. Users
    // paste in different orders; row 2 in the user's screenshot even has a
    // space-typo separator inside a cell. Tokenize each cell on whitespace
    // and bucket every token by pattern, then assemble the row.
    const EMAIL_RE  = /^[^@\s,]+@[^@\s,]+\.[^@\s,]+$/;
    const DOMAIN_RE = /^[a-z0-9-]+(\.[a-z0-9-]+)+$/i;
    const NAME_TOKEN_RE = /^[A-Za-z][A-Za-z'-]+$/;   // single capitalized word
    const looksLikeName = (s: string) => {
      const parts = s.trim().split(/\s+/);
      return parts.length >= 2 && parts.length <= 4 && parts.every(p => NAME_TOKEN_RE.test(p));
    };

    const parsed: ParsedRow[] = dataRows.map(cols => {
      let ga_upn = '', ga_password = '', target_domain = '', name_seed = '';
      const leftover: string[] = [];

      for (const raw_cell of cols) {
        const cell = (raw_cell || '').trim();
        if (!cell) continue;
        // A name like "Alex Torres" must stay together as one cell, not split.
        if (looksLikeName(cell) && !name_seed) {
          name_seed = cell;
          continue;
        }
        // Otherwise split on whitespace inside the cell — handles
        // "admin@x.com jnmmLjj7+" being one CSV cell with a space typo.
        for (const tok of cell.split(/\s+/).filter(Boolean)) {
          if (!ga_upn && EMAIL_RE.test(tok))                ga_upn = tok;
          else if (!target_domain && DOMAIN_RE.test(tok) && !tok.includes('@'))
                                                            target_domain = tok;
          else                                              leftover.push(tok);
        }
      }
      // First leftover token = password. Anything beyond is suspicious; leave it.
      ga_password = leftover.shift() || '';

      const out: any = {
        ga_upn,
        ga_password,
        target_domain,
        name_seed: name_seed || undefined,
      };
      const errors = validateRow(out);
      if (leftover.length) errors.push(`unparsed: ${leftover.join(' ')}`);
      return { ...out, _errors: errors } as ParsedRow;
    });
    // dedupe target_domain across rows
    const seen = new Set<string>();
    for (const r of parsed) {
      if (r.target_domain) {
        if (seen.has(r.target_domain)) r._errors.push('duplicate target_domain');
        else seen.add(r.target_domain);
      }
    }
    setRows(parsed);
    setRaw('');  // clear the raw paste so plaintext passwords don't linger
  }

  async function submit() {
    if (!rows) return;
    const valid = rows.filter(r => r._errors.length === 0);
    if (!valid.length) { setErr('no valid rows'); return; }
    setBusy(true); setErr(null);
    try {
      const payload = valid.map(({ _errors, ...r }) => ({
        ...r,
        client_slug: r.client_slug || defaultClient || undefined,
      }));
      await api.createTenantsBulk(payload);
      nav('/');
    } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  }

  const validCount = rows?.filter(r => !r._errors.length).length ?? 0;

  return (
    <div className="space-y-4">
      {!rows ? (
        <div className="card p-5 space-y-4">
          {/* Required fields, in plain language. Order doesn't matter —
              each cell is classified by content, so columns can be in
              any order. */}
          <div>
            <div className="text-xs uppercase tracking-wider text-dim mb-2">Required per row</div>
            <ul className="text-sm space-y-1.5">
              <li><strong>Tenant login</strong> — <code className="text-dim">admin@xxx.onmicrosoft.com</code></li>
              <li><strong>Tenant password</strong></li>
              <li><strong>Target domain</strong> — <code className="text-dim">yourdomain.com</code></li>
              <li><strong>Mailbox name</strong> <span className="text-dim">(optional)</span> — <code className="text-dim">John Smith</code></li>
            </ul>
            <p className="text-[11px] text-dim mt-3 leading-relaxed">
              Columns can be in any order — Sendepot classifies each cell
              by content. A header row is auto-detected and skipped.
            </p>
          </div>

          {/* Sequencer routing — only relevant once the operator has set
              up at least one labeled key. */}
          <details className="text-xs">
            <summary className="text-dim hover:text-ink cursor-pointer">
              Routing rows to a specific sequencer account →
            </summary>
            <div className="mt-2 pl-3 border-l border-line/60 text-dim leading-relaxed space-y-1.5">
              <p>
                Add a header row including a <code>sequencer_key_label</code>{' '}
                column (aliases: <code>account</code>, <code>account_label</code>,{' '}
                <code>instantly_account</code>) matching a label you set in{' '}
                <a href="/settings" className="text-accent hover:underline">Settings → Sequencer API keys</a>.
              </p>
              <p>
                Rows without a label fall back to the workspace's default
                key for the sequencer.
              </p>
            </div>
          </details>

          {isAdmin && (
            <label className="block">
              <div className="text-xs text-dim mb-1.5">Assign all rows to client (optional)</div>
              <select className="input" value={defaultClient} onChange={e => setDefaultClient(e.target.value)}>
                <option value="">— Unassigned —</option>
                {(clients || []).map(c => (
                  <option key={c.id} value={c.slug}>{c.name}</option>
                ))}
              </select>
            </label>
          )}

          <div>
            <div className="text-xs uppercase tracking-wider text-dim mb-1.5">Paste CSV</div>
            <textarea
              className="input font-mono text-xs h-64"
              placeholder={'admin@xxx.onmicrosoft.com,password,example.com,John Smith\nadmin@yyy.onmicrosoft.com,password,another.com'}
              value={raw} onChange={e => setRaw(e.target.value)}
            />
          </div>
          {err && <div className="text-err text-xs">{err}</div>}
          <div className="flex justify-end">
            <button onClick={parse} disabled={!raw.trim()} className="btn-primary">Preview</button>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="p-3 border-b border-line text-xs text-dim flex justify-between">
            <span>{validCount} of {rows.length} valid</span>
            <button onClick={() => setRows(null)} className="text-dim hover:text-ink">← re-paste</button>
          </div>
          <table className="w-full text-xs">
            <thead className="bg-bg">
              <tr className="text-left text-dim">
                <th className="p-2">#</th><th>Tenant login</th><th>Password</th><th>Domain</th><th>Name</th><th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-t border-line/50">
                  <td className="p-2 text-dim">{i + 1}</td>
                  <td className="p-2">{r.ga_upn || <span className="text-err">missing</span>}</td>
                  <td className="p-2 text-dim">{r.ga_password ? '••••' : <span className="text-err">missing</span>}</td>
                  <td className="p-2">{r.target_domain || <span className="text-err">missing</span>}</td>
                  <td className="p-2 text-dim">{r.name_seed || <span className="opacity-50">random</span>}</td>
                  <td className="p-2">
                    {r._errors.length === 0
                      ? <span className="text-ok">✓ ready</span>
                      : <span className="text-err">✗ {r._errors.join(', ')}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="p-3 border-t border-line flex justify-between items-center">
            {err && <div className="text-err text-xs">{err}</div>}
            <button onClick={submit} disabled={busy || validCount === 0} className="btn-primary ml-auto">
              Import {validCount} tenant{validCount === 1 ? '' : 's'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
